import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { MessageSquare, Download, RefreshCw, AlertCircle, TrendingUp, Heart, BarChart3, FileJson, FileSpreadsheet, CreditCard, ExternalLink } from 'lucide-react';
import { exportCommentsCSV, exportCommentsJSON, getCreditBalance } from '../../lib/api';
import { useEnhancedComments } from '../../hooks/useEnhancedComments';
import { CommentsPreview } from './CommentsPreview';
import { CreditWarningModal } from './CreditWarningModal';
import { CommentsProgress } from './CommentsProgress';
interface CommentAnalysisProps {
  taskId: string;
  videoId?: string;
}
export function CommentAnalysis({
  taskId,
  videoId
}: CommentAnalysisProps) {
  const {
    preview,
    previewLoading,
    previewError,
    fetching,
    progress,
    results,
    showCreditModal,
    setShowCreditModal,
    alreadyFetched,
    insufficientCredits,
    creditsError,
    initiateFetch
  } = useEnhancedComments(taskId);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [currentCredits, setCurrentCredits] = useState<number | null>(null);
  // Load current credit balance when insufficient credits error occurs
  useEffect(() => {
    if (insufficientCredits) {
      loadCredits();
    }
  }, [insufficientCredits]);
  const loadCredits = async () => {
    try {
      const balance = await getCreditBalance();
      setCurrentCredits(balance);
    } catch (error) {
      console.error('Error loading credits:', error);
    }
  };
  const handleFetchClick = () => {
    setShowCreditModal(true);
  };
  const handleConfirmFetch = () => {
    initiateFetch();
  };
  const handleExportCSV = async () => {
    setIsExporting(true);
    setExportError(null);
    try {
      await exportCommentsCSV(taskId);
    } catch (err) {
      console.error('Error exporting CSV:', err);
      setExportError('Failed to export CSV. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };
  const handleExportJSON = async () => {
    setIsExporting(true);
    setExportError(null);
    try {
      await exportCommentsJSON(taskId);
    } catch (err) {
      console.error('Error exporting JSON:', err);
      setExportError('Failed to export JSON. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };
  // Show insufficient credits error
  if (insufficientCredits && creditsError) {
    return <div className="border border-destructive/30 rounded-lg bg-destructive/5" data-id="element-1106">
        <div className="p-4" data-id="element-1107">
          <div className="flex items-start gap-3 mb-4" data-id="element-1108">
            <AlertCircle size={20} className="text-destructive mt-0.5 flex-shrink-0" data-id="element-1109" />
            <div className="flex-grow" data-id="element-1110">
              <h4 className="font-medium text-sm text-destructive mb-1" data-id="element-1111">
                Insufficient Credits
              </h4>
              <p className="text-sm text-muted-foreground mb-3" data-id="element-1112">
                {creditsError}
              </p>
              {preview && <div className="bg-secondary/30 rounded-lg p-3 mb-3" data-id="element-1113">
                  <div className="grid grid-cols-2 gap-3 text-xs" data-id="element-1114">
                    <div data-id="element-1115">
                      <span className="text-muted-foreground" data-id="element-1116">
                        Estimated comments:
                      </span>
                      <span className="font-medium ml-2" data-id="element-1117">
                        {preview.estimated_total.toLocaleString()}
                      </span>
                    </div>
                    <div data-id="element-1118">
                      <span className="text-muted-foreground" data-id="element-1119">
                        Credits needed:
                      </span>
                      <span className="font-medium ml-2" data-id="element-1120">
                        {preview.credits_needed}
                      </span>
                    </div>
                    {currentCredits !== null && <>
                        <div data-id="element-1121">
                          <span className="text-muted-foreground" data-id="element-1122">
                            Your balance:
                          </span>
                          <span className="font-medium ml-2" data-id="element-1123">
                            {currentCredits} credits
                          </span>
                        </div>
                        <div data-id="element-1124">
                          <span className="text-muted-foreground" data-id="element-1125">Need:</span>
                          <span className="font-medium ml-2 text-destructive" data-id="element-1126">
                            {Math.max(0, preview.credits_needed - currentCredits)}{' '}
                            more
                          </span>
                        </div>
                      </>}
                  </div>
                </div>}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2" data-id="element-1127">
            <Button variant="default" size="sm" className="flex-1" onClick={() => window.open('/app/settings', '_blank')} data-id="element-1128">
              <CreditCard size={14} className="mr-2" data-id="element-1129" />
              Buy Credits
            </Button>
            <Button variant="outline" size="sm" className="flex-1" onClick={() => window.location.reload()} data-id="element-1130">
              <RefreshCw size={14} className="mr-2" data-id="element-1131" />
              Refresh
            </Button>
          </div>

          <div className="mt-3 pt-3 border-t border-border/30" data-id="element-1132">
            <p className="text-xs text-muted-foreground" data-id="element-1133">
              💡 <strong data-id="element-1134">How it works:</strong> 1 credit = 100 comments. Credits
              are only charged after successful analysis.
            </p>
          </div>
        </div>
      </div>;
  }
  // Show progress if fetching
  if (fetching && progress) {
    return <CommentsProgress progress={progress} data-id="element-1135" />;
  }
  // Show results if completed
  if (results && !fetching) {
    return <div className="border border-primary/30 rounded-lg bg-card" data-id="element-1136">
        <div className="p-4 border-b border-border/30" data-id="element-1137">
          <div className="flex items-center justify-between mb-3" data-id="element-1138">
            <div className="flex items-center gap-2" data-id="element-1139">
              <MessageSquare size={16} className="text-primary" data-id="element-1140" />
              <h4 className="font-medium text-sm" data-id="element-1141">Comment Analysis Results</h4>
            </div>
            <div className="flex items-center gap-2" data-id="element-1142">
              <Button variant="ghost" size="sm" onClick={handleExportCSV} disabled={isExporting} className="h-8 px-2" data-id="element-1143">
                <FileSpreadsheet size={14} className="mr-1" data-id="element-1144" />
                CSV
              </Button>
              <Button variant="ghost" size="sm" onClick={handleExportJSON} disabled={isExporting} className="h-8 px-2" data-id="element-1145">
                <FileJson size={14} className="mr-1" data-id="element-1146" />
                JSON
              </Button>
            </div>
          </div>

          {exportError && <div className="mb-3 p-2 bg-destructive/10 border border-destructive/20 rounded text-xs text-destructive flex items-center gap-2" data-id="element-1147">
              <AlertCircle size={14} data-id="element-1148" />
              {exportError}
            </div>}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-id="element-1149">
            <div className="bg-secondary/50 rounded-lg p-3" data-id="element-1150">
              <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1" data-id="element-1151">
                <MessageSquare size={12} data-id="element-1152" />
                Total Comments
              </div>
              <div className="text-lg font-semibold" data-id="element-1153">
                {results.total_comments}
              </div>
            </div>
            <div className="bg-secondary/50 rounded-lg p-3" data-id="element-1154">
              <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1" data-id="element-1155">
                <Heart size={12} data-id="element-1156" />
                Avg Likes
              </div>
              <div className="text-lg font-semibold" data-id="element-1157">
                {Math.round(results.avg_likes).toLocaleString()}
              </div>
            </div>
            <div className="bg-secondary/50 rounded-lg p-3" data-id="element-1158">
              <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1" data-id="element-1159">
                <TrendingUp size={12} data-id="element-1160" />
                Engagement
              </div>
              <div className="text-lg font-semibold" data-id="element-1161">
                {results.engagement_rate.toFixed(1)}%
              </div>
            </div>
            <div className="bg-secondary/50 rounded-lg p-3" data-id="element-1162">
              <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1" data-id="element-1163">
                <BarChart3 size={12} data-id="element-1164" />
                Sentiment
              </div>
              <div className="text-sm font-semibold capitalize" data-id="element-1165">
                {results.sentiment.dominant} ({results.sentiment.positive}%)
              </div>
            </div>
          </div>
        </div>

        <div className="p-4" data-id="element-1166">
          <h5 className="text-sm font-medium mb-3 flex items-center gap-2" data-id="element-1167">
            <TrendingUp size={14} className="text-primary" data-id="element-1168" />
            Top Comments
          </h5>

          <div className="overflow-x-auto" data-id="element-1169">
            <table className="w-full text-sm" data-id="element-1170">
              <thead className="border-b border-border/30" data-id="element-1171">
                <tr className="text-left" data-id="element-1172">
                  <th className="pb-2 pr-4 font-medium text-muted-foreground" data-id="element-1173">
                    Author
                  </th>
                  <th className="pb-2 pr-4 font-medium text-muted-foreground" data-id="element-1174">
                    Comment
                  </th>
                  <th className="pb-2 pr-4 font-medium text-muted-foreground text-right" data-id="element-1175">
                    Likes
                  </th>
                  <th className="pb-2 font-medium text-muted-foreground text-right" data-id="element-1176">
                    Replies
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1177">
                {results.top_comments.map((comment, index) => <tr key={index} className="border-b border-border/20 last:border-0" data-id="element-1178">
                    <td className="py-3 pr-4 text-xs" data-id="element-1179">{comment.author}</td>
                    <td className="py-3 pr-4 text-xs max-w-xs truncate" data-id="element-1180">
                      {comment.comment || comment.text}
                    </td>
                    <td className="py-3 pr-4 text-xs text-right" data-id="element-1181">
                      {comment.likes.toLocaleString()}
                    </td>
                    <td className="py-3 text-xs text-right" data-id="element-1182">
                      {comment.replies}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          <div className="mt-4 pt-4 border-t border-border/20 flex items-center justify-between" data-id="element-1183">
            <p className="text-xs text-muted-foreground" data-id="element-1184">
              Showing {results.top_comments.length} of {results.total_comments}{' '}
              comments
            </p>
            <Button variant="outline" size="sm" onClick={() => window.location.reload()} data-id="element-1185">
              <RefreshCw size={14} className="mr-1" data-id="element-1186" />
              Refresh
            </Button>
          </div>
        </div>
      </div>;
  }
  // Show preview by default
  return <>
      <CommentsPreview preview={preview} loading={previewLoading} error={previewError} alreadyFetched={alreadyFetched} onFetchClick={handleFetchClick} data-id="element-1187" />

      <CreditWarningModal isOpen={showCreditModal} onClose={() => setShowCreditModal(false)} onConfirm={handleConfirmFetch} creditsNeeded={preview?.credits_needed || 0} estimatedTotal={preview?.estimated_total || 0} data-id="element-1188" />
    </>;
}